import { Backdrop, Box, Button, CircularProgress, Grid, makeStyles, TextField, Typography } from '@material-ui/core'
import firebase from 'firebase/compat/app'
import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSearchParam } from 'react-use'

import backgroundImage from '~/assets/images/piazza-cestello.png'
import { ReactComponent as NbLogo } from '~/assets/images/nb-logo.svg'
import ErrorSnackbar from '~/components/error-snackbar'
import Flex from '~/components/flex'

const useStyles = makeStyles((theme) => ({
	background: {
		backgroundColor: '#0A24EB',
		backgroundPosition: 'center',
		backgroundImage: `url(${backgroundImage})`,
		backgroundSize: 'cover',
		minWidth: '100%',
		minHeight: '100%',
		position: 'absolute'
	},
	button: {
		fontFamily: 'halyard-display',
		textTransform: 'none',
		marginTop: theme.spacing(2),
		width: '100%',
		backgroundColor: 'white',
		color: '#0A24EB',
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.8)',
		}
	},
	loadingBackdrop: {
		color: theme.palette.common.white,
		zIndex: theme.zIndex.modal,
	},
	logo: {
		fill: theme.palette.common.white,
		maxWidth: 256,
	},
	form: {
		width: '100%',
		maxWidth: 400,
	},
	textField: {
		marginBottom: theme.spacing(2),
		'& .MuiInputBase-root': {
			color: 'white',
		},
		'& .MuiInputLabel-root': {
			color: 'rgba(255, 255, 255, 0.7)',
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: 'rgba(255, 255, 255, 0.5)',
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: 'white',
		},
		'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: 'white',
		},
	},
	backLink: {
		color: 'white',
		textDecoration: 'underline',
		cursor: 'pointer',
		marginTop: theme.spacing(3),
		fontSize: '0.875rem',
		textAlign: 'center',
	}
}))

const getDataFromUserId = async (userId: string) => {
	const documentRef = firebase.firestore().collection('users').doc(userId)
	const document = await documentRef.get()

	if (document?.exists) {
		return document.data()
	}

	const documentSnapshot = await new Promise<firebase.firestore.DocumentSnapshot>((resolve, reject) =>{
		const observer = documentRef.onSnapshot(
		  (documentSnapshot) => {
			if (documentSnapshot.exists) {
			  observer();
			  resolve(documentSnapshot);
			}
		  },
		  (error) => {
			observer();
			reject(error);
		  }
		);
	  });

	return documentSnapshot.data()
}

const EmailPasswordLogin: React.FC = () => {
	const navigate = useNavigate()
	const classes = useStyles()
	const inviteToTeam = useSearchParam('inviteToTeam');

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')

	const handleLogin = useCallback(async (e: React.FormEvent) => {
		e.preventDefault()
		setLoading(true)
		try {
			const userCredentials = await firebase
				.auth()
				.signInWithEmailAndPassword(email, password)

			const user = userCredentials.user
			if (user && user.uid) {
				await getDataFromUserId(user.uid)
			}

			const urlParams = new URLSearchParams(window.location.search);
			const redirectUrl = urlParams.get('redirect')

			if (inviteToTeam) {
				navigate('/joinTeam' + inviteToTeam, { replace: true })
			} else if (redirectUrl != null) {
				navigate(redirectUrl, { replace: true })
			} else {
				navigate('/', { replace: true })
			}
		} catch (error) {
			setError((error as Error).message)
		} finally {
			setLoading(false)
		}
	}, [email, password, navigate, inviteToTeam])

	const handleErrorSnackbarClose = useCallback(() => {
		setError(null)
	}, [])

	return (
		<>
			<Backdrop className={classes.loadingBackdrop} open={loading}>
				<CircularProgress color='inherit' />
			</Backdrop>
			<Flex className={classes.background} alignItems='center' justifyContent='center'>
				<Flex flexDirection='column' alignItems='center' px={3} margin={3} className={classes.form}>
					<Box paddingBottom={5}>
						<NbLogo className={classes.logo} title='logo' />
					</Box>
					<Typography variant='h4' align='center' style={{ color: 'white', marginBottom: '24px' }}>
						Accedi con Email
					</Typography>
					<form onSubmit={handleLogin} style={{ width: '100%' }}>
						<TextField
							className={classes.textField}
							label="Email"
							variant="outlined"
							fullWidth
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							disabled={loading}
							autoFocus
						/>
						<TextField
							className={classes.textField}
							label="Password"
							type="password"
							variant="outlined"
							fullWidth
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							disabled={loading}
						/>
						<Button
							type="submit"
							className={classes.button}
							variant='contained'
							disabled={loading || !email || !password}
						>
							Accedi
						</Button>
					</form>
					<Typography
						className={classes.backLink}
						onClick={() => navigate('/login' + window.location.search)}
					>
						Torna al login social
					</Typography>
				</Flex>
			</Flex>
			<ErrorSnackbar
				error={error}
				onClose={handleErrorSnackbarClose}
			/>
		</>
	)
}

export default EmailPasswordLogin
